import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useGetEligibilityWorklistQuery } from '@features/patient-coverages';
import { useCan } from '@features/user';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import type { WorklistTotals } from '@store/generated/eligibility';

type StatusTone = 'default' | 'warning' | 'error' | 'success';

interface EligibilityStatusSummary {
  key: string;
  label: string;
  count: number;
  tone: StatusTone;
}

export interface EligibilityChecksSummaryCardContentProps {
  isLoading?: boolean;
  totals?: WorklistTotals;
}

const TONE_COLORS: Record<
  StatusTone,
  'text.secondary' | 'warning.main' | 'error.main' | 'success.main'
> = {
  default: 'text.secondary',
  warning: 'warning.main',
  error: 'error.main',
  success: 'success.main',
};

export const EligibilityChecksSummaryCard: React.FC = () => {
  const hasEligibilityChecks = useFeatureGate(FeatureGates.ELIGIBILITY_CHECKS);
  const canManageEligibility = useCan('manage_eligibility');
  const hasWorklistAccess = hasEligibilityChecks && canManageEligibility;
  const { data, isLoading } = useGetEligibilityWorklistQuery(
    { days: 7, perPage: 1 },
    { skip: !hasWorklistAccess },
  );

  if (!hasWorklistAccess) return null;

  return <EligibilityChecksSummaryCardContent isLoading={isLoading} totals={data?.totals} />;
};

export const EligibilityChecksSummaryCardContent: React.FC<
  EligibilityChecksSummaryCardContentProps
> = ({ isLoading = false, totals }) => {
  const theme = useTheme();
  const statuses: EligibilityStatusSummary[] = [
    {
      key: 'awaiting_check',
      label: 'Awaiting',
      count: totals?.awaiting_check ?? 0,
      tone: 'default',
    },
    {
      key: 'needs_attention',
      label: 'Review',
      count: totals?.needs_attention ?? 0,
      tone: 'warning',
    },
    {
      key: 'needs_coverage',
      label: 'Coverage',
      count: totals?.needs_coverage ?? 0,
      tone: 'error',
    },
    {
      key: 'verified',
      label: 'Verified',
      count: totals?.verified ?? 0,
      tone: 'success',
    },
  ];

  return (
    <Box
      sx={{
        mt: 1.5,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
        }}
      >
        <FactCheckOutlinedIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: theme.palette.text.primary }}
          >
            Eligibility
          </Typography>
          {isLoading ? (
            <Skeleton width={84} height={16} />
          ) : (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {totals?.total_patients_in_window ?? 0} upcoming checks
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ px: 1.5, py: 1.25 }}>
        {isLoading ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={30} />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            {statuses.map(status => {
              const color = TONE_COLORS[status.tone];
              return (
                <Box
                  key={status.key}
                  sx={{
                    minWidth: 0,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.grey[500], 0.08),
                    px: 1,
                    py: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: theme.palette.text.secondary,
                      lineHeight: 1.2,
                    }}
                    noWrap
                  >
                    {status.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      color,
                      lineHeight: 1.25,
                    }}
                  >
                    {status.count > 99 ? '99+' : status.count}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        <Button
          component={RouterLink}
          to="/jotbill?tab=verifications"
          size="small"
          variant="text"
          fullWidth
          sx={{
            mt: 1,
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontWeight: 650,
            px: 0.5,
          }}
        >
          View checks
        </Button>
      </Box>
    </Box>
  );
};
